import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BudgetSummary, Client, Project } from '@bpd/shared';
import { formatCurrencyRounded } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { LedgerTable } from '../../components/LedgerTable.tsx';
import { SearchWidget } from '../../components/search/SearchWidget.tsx';
import { GateBadges } from '../../components/projects/GateBadges.tsx';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';
type ProjectsState = 'loading' | 'error' | Project[];
type TaskStats = { done: number; total: number };
type TaskStatsState = 'loading' | 'error' | TaskStats;

export function ClientsPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [clients, setClients] = useState<Client[]>([]);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [projectsByClient, setProjectsByClient] = useState<Record<string, ProjectsState>>({});
  const [taskStatsByProject, setTaskStatsByProject] = useState<Record<string, TaskStatsState>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const filteredClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.label, c.contactName, c.contactEmail].some((f) => f && f.toLowerCase().includes(q)),
    );
  }, [clients, searchQuery]);

  function toggleExpand(clientId: string) {
    const next = expandedId === clientId ? null : clientId;
    setExpandedId(next);
    if (next && !projectsByClient[next]) {
      setProjectsByClient((prev) => ({ ...prev, [next]: 'loading' }));
      projectsApi
        .listProjects(next)
        .then((data) => {
          setProjectsByClient((prev) => ({ ...prev, [next]: data }));
          data.forEach((p) => {
            setTaskStatsByProject((prev) => ({ ...prev, [p.id]: 'loading' }));
            projectsApi
              .listItems('project', p.id)
              .then((items) =>
                setTaskStatsByProject((prev) => ({
                  ...prev,
                  [p.id]: {
                    done: items.filter((i) => i.status === 'complete').length,
                    total: items.length,
                  },
                })),
              )
              .catch(() => setTaskStatsByProject((prev) => ({ ...prev, [p.id]: 'error' })));
          });
        })
        .catch(() => setProjectsByClient((prev) => ({ ...prev, [next]: 'error' })));
    }
  }

  function load() {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([projectsApi.listClients(), projectsApi.getBudget()])
      .then(([data, budgetSummary]) => {
        setClients(data);
        setBudget(budgetSummary);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createClient({
        label: label.trim(),
        ...(contactName ? { contactName } : {}),
        ...(contactPhone ? { contactPhone } : {}),
        ...(contactEmail ? { contactEmail } : {}),
      });
      setClients((prev) => [...prev, created]);
      setLabel('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setShowAdd(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add client');
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">
          Clients &amp; Accounts
        </h1>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
        >
          {showAdd ? 'Cancel' : '+ Add client'}
        </button>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Properties you&apos;re managing projects for — your own homes and paying clients alike.
      </p>

      <div className="mt-4">
        <SearchWidget categories={['clients']} onQueryChange={setSearchQuery} />
      </div>

      {showAdd && (
        <form
          onSubmit={(e) => void handleAdd(e)}
          className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
        >
          <input
            type="text"
            placeholder="Label — e.g. 5160 Lake Grove Road *"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={200}
            required
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Contact name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <input
              type="text"
              placeholder="Contact phone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <input
              type="email"
              placeholder="Contact email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add client'}
          </button>
        </form>
      )}

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load clients. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && (
        <div className="mt-6 space-y-2">
          {clients.length === 0 && (
            <p className="text-sm text-gray-400">No clients yet — add one above.</p>
          )}
          {clients.length > 0 && filteredClients.length === 0 && (
            <p className="text-sm text-gray-400">No clients match &quot;{searchQuery.trim()}&quot;.</p>
          )}
          {filteredClients.map((c) => {
            const isExpanded = expandedId === c.id;
            const projectsState = projectsByClient[c.id];
            return (
              <div
                key={c.id}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-phoenix-blue-300 hover:shadow-md"
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => toggleExpand(c.id)}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse projects' : 'Expand projects'}
                    className="mt-0.5 shrink-0 text-gray-400 hover:text-phoenix-blue-600"
                  >
                    <span
                      className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    >
                      ▶
                    </span>
                  </button>
                  <Link to={`/admin/projects/clients/${c.id}`} className="block flex-1">
                    <p className="font-semibold text-gray-900">{c.label}</p>
                    {(c.contactName || c.contactPhone || c.contactEmail) && (
                      <p className="mt-0.5 text-sm text-gray-500">
                        {[c.contactName, c.contactPhone, c.contactEmail]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    )}
                    {(() => {
                      const t = budget?.clientTotals.find((ct) => ct.clientId === c.id);
                      if (!t || t.totalProjectCost <= 0) return null;
                      return (
                        <div className="mt-2 max-w-xs">
                          <LedgerTable
                            size="sm"
                            rows={[
                              {
                                label: 'Total project cost',
                                value: formatCurrencyRounded(t.totalProjectCost),
                              },
                              {
                                label: 'Remaining balance due',
                                value: formatCurrencyRounded(t.remainingBalanceDue),
                                accent: true,
                              },
                            ]}
                          />
                        </div>
                      );
                    })()}
                  </Link>
                </div>

                {isExpanded && (
                  <div className="ml-6 mt-3 space-y-1 border-t border-gray-100 pt-3">
                    {projectsState === 'loading' && (
                      <p className="text-sm text-gray-400">Loading projects…</p>
                    )}
                    {projectsState === 'error' && (
                      <p className="text-sm text-red-600">Couldn&apos;t load projects.</p>
                    )}
                    {Array.isArray(projectsState) && projectsState.length === 0 && (
                      <p className="text-sm text-gray-400">No projects yet.</p>
                    )}
                    {Array.isArray(projectsState) &&
                      projectsState.map((p) => {
                        const stats = taskStatsByProject[p.id];
                        const pb = budget?.projects.find((b) => b.projectId === p.id);
                        return (
                          <Link
                            key={p.id}
                            to={`/admin/projects/clients/${c.id}/${p.id}`}
                            className="flex flex-col gap-1 rounded px-2 py-1 text-sm text-gray-700 hover:bg-phoenix-blue-50 hover:text-phoenix-blue-700"
                          >
                            <span className="flex items-center justify-between gap-3">
                              <span className="min-w-0 flex-1 truncate">
                                {p.name}
                                {typeof stats === 'object' && (
                                  <span className="ml-1.5 text-gray-400">
                                    ({stats.done}/{stats.total})
                                  </span>
                                )}
                              </span>
                              <span className="flex shrink-0 gap-3 tabular-nums">
                                <span className="w-20 text-right font-bold text-gray-900">
                                  {formatCurrencyRounded(pb?.totalProjectCost ?? 0)}
                                </span>
                                {/* Hidden below sm: with a fixed-width delete-free row already tight on
                                    mobile, the name column loses the truncation fight to two fixed-width
                                    currency columns. Only the bold total (the number that matters most)
                                    stays visible; balance due reappears once there's room for both. */}
                                <span className="hidden w-20 text-right font-normal text-red-600 sm:inline-block">
                                  {formatCurrencyRounded(pb?.remainingBalanceDue ?? 0)}
                                </span>
                              </span>
                            </span>
                            {pb && <GateBadges gates={pb.gates} mode="compact" />}
                          </Link>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
